import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, PageTitle, CommonCss, Card } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { SEARCH_LABEL_W } from 'styles/theme';
import {
  Meta,
  ModeTabBar,
  ModeTab,
} from 'components/system/SystemPageChrome.styles';

import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { loadDepts, getCachedDepts } from 'master/deptStore';
import {
  ACCOUNT_TYPE_OPTIONS,
  ACCOUNT_STATUS_OPTIONS,
  MEMBERSHIP_STATUS_OPTIONS,
  createAccountUid,
  loadUsers,
  persistUsers,
  loadUserOrgMaps,
  filterUsers,
  validateUsers,
  validateUserOrgMaps,
} from 'master/userAccountStore';

const USER_GRID_ID = 'master-user-account';
const ORG_GRID_ID = 'master-user-org-map';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  flex: ${(p) => p.$flex || 1};
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;


/**
 * 기준정보 — 사용자관리
 *
 * CM-DESIGN-001 계약:
 * - 기관 내 계정 수명주기 관리
 * - 기본 1사용자 1부서(주관부서)는 사용자 그리드에서 팝업으로 등록
 * - 겸직·중복 소속은 하단 겸임부서 그리드에 예외 등록 (주관부서와 중복 금지)
 * - 권한은 주관부서 + ACTIVE 겸임부서 권한의 합집합으로 계산
 * - 로그인 ID는 최초 저장 후 불변
 */
const UserManageInner = () => {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const userGridRef = useRef(null);
  const orgGridRef = useRef(null);
  const selectedUidRef = useRef('');
  const allOrgMapsRef = useRef([]);
  const [selectedHint, setSelectedHint] = useState('사용자를 선택하면 겸임부서를 관리할 수 있습니다.');
  // 부서 팝업 — type: 'primary'(주관부서) | 'concurrent'(겸임부서), target: accountUid 또는 mapUid
  const [deptLookup, setDeptLookup] = useState({ open: false, type: null });
  const lookupTargetRef = useRef('');

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } =
    useActiveGridCrudActions({
      guardAdd: (id) =>
        id === ORG_GRID_ID && !selectedUidRef.current
          ? '사용자를 먼저 선택하세요.'
          : null,
      guardDelete: (id) =>
        id === ORG_GRID_ID && !selectedUidRef.current
          ? '사용자를 먼저 선택하세요.'
          : null,
    });

  const searchDefaults = useMemo(
    () => ({ tenantCode, loginId: '', userNm: '', status: 'ACTIVE' }),
    [tenantCode]
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const [deptRows, setDeptRows] = useState(() =>
    (getCachedDepts(tenantCode) || []).filter((r) => r.useYn !== 'N')
  );
  useEffect(() => {
    let cancelled = false;
    loadDepts(tenantCode)
      .then((rows) => {
        if (!cancelled) setDeptRows((rows || []).filter((r) => r.useYn !== 'N'));
      })
      .catch(() => {
        if (!cancelled) setDeptRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  const openDeptLookup = useCallback(
    (type, row) => {
      let excluded = new Set();
      if (type === 'primary') {
        if (!row?.accountUid) return;
        lookupTargetRef.current = row.accountUid;
        // 이미 겸임부서로 등록된 부서는 주관부서 후보에서 제외
        const maps =
          row.accountUid === selectedUidRef.current
            ? (orgGridRef.current?.getData?.() || []).filter((r) => r.mod !== 'D')
            : allOrgMapsRef.current.filter((r) => r.accountUid === row.accountUid);
        excluded = new Set(maps.map((r) => r.deptUid).filter(Boolean));
      } else {
        if (!row?.mapUid) return;
        lookupTargetRef.current = row.mapUid;
        // 주관부서 + 이미 등록된 다른 겸임부서 제외 (예: 주관 a팀이면 팝업에서 a팀 미노출)
        const userRow = (userGridRef.current?.getData?.() || []).find(
          (r) => r.accountUid === selectedUidRef.current
        );
        excluded = new Set(
          (orgGridRef.current?.getData?.() || [])
            .filter((r) => r.mod !== 'D' && r.mapUid !== row.mapUid)
            .map((r) => r.deptUid)
            .filter(Boolean)
        );
        if (userRow?.deptUid) excluded.add(userRow.deptUid);
      }
      const rows = deptRows
        .filter((d) => !excluded.has(d.deptUid))
        .map((d) => ({ ...d, id: d.deptUid }));
      setDeptLookup({ open: true, type, rows });
    },
    [deptRows]
  );

  const deptLookupColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
      hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 200 }),
    ],
    []
  );

  /** 팝업 선택 부서를 대상 행에 반영 */
  const applyDeptLookup = useCallback(
    (dept) => {
      const target = lookupTargetRef.current;
      if (!dept?.deptUid || !target) return;
      const patch = {
        deptUid: dept.deptUid,
        deptCode: dept.deptCode || '',
        deptName: dept.deptName || '',
      };
      if (deptLookup.type === 'primary') {
        const rows = userGridRef.current?.getData?.() || [];
        const idx = rows.findIndex((r) => r.accountUid === target);
        if (idx < 0) return;
        userGridRef.current?.setCellValues?.(idx, {
          ...patch,
          mod: rows[idx].mod === 'A' ? 'A' : 'U',
        });
        if (target === selectedUidRef.current) {
          const row = rows[idx];
          setSelectedHint(
            `${row.loginId || '(ID 미입력)'} · ${row.userNm || ''} · 주관 ${patch.deptName}`
          );
        }
      } else {
        const rows = orgGridRef.current?.getData?.() || [];
        const idx = rows.findIndex((r) => r.mapUid === target);
        if (idx < 0) return;
        orgGridRef.current?.setCellValues?.(idx, {
          ...patch,
          mod: rows[idx].mod === 'A' ? 'A' : 'U',
        });
      }
    },
    [deptLookup.type]
  );

  const userColumns = useMemo(
    () => [
      hiCol.edTextColumnForKey({
        header: '사용자ID',
        field: 'loginId',
        size: 120,
        required: true,
        title: '최초 등록 시에만 입력 가능하며 저장 후에는 변경할 수 없습니다.',
      }),
      hiCol.edTextColumn({
        header: '비밀번호',
        field: 'password',
        size: 110,
        title: '신규 필수 · 기존 계정은 변경할 때만 입력',
      }),
      hiCol.edTextColumn({ header: '사용자명', field: 'userNm', size: 110, required: true }),
      // 주관부서 — 기본 1사용자 1부서, 팝업으로만 등록 (직접 입력 불가)
      hiCol.edTextColumn({
        header: '주관부서',
        field: 'deptName',
        size: 150,
        required: true,
        editable: false,
        showSearch: true,
        searchTitle: '부서 조회 (팝업)',
        title: '기본 소속(주관) 부서 · 겸직은 하단 겸임부서에 등록',
        onSearch: ({ row }) => openDeptLookup('primary', row),
      }),
      hiCol.edTextColumn({ header: '이메일', field: 'email', size: 190 }),
      hiCol.edTextColumn({ header: '휴대전화', field: 'phone', size: 130 }),
      hiCol.edComboColumn({
        header: '계정유형',
        field: 'accountType',
        size: 120,
        options: ACCOUNT_TYPE_OPTIONS,
        required: true,
      }),
      hiCol.edComboColumn({
        header: '계정상태',
        field: 'status',
        size: 100,
        options: ACCOUNT_STATUS_OPTIONS,
        required: true,
      }),
      hiCol.roNumberColumn({
        header: '로그인실패',
        field: 'failedLoginCnt',
        size: 90,
        align: 'center',
      }),
      hiCol.roTextColumn({ header: '최근로그인', field: 'lastLoginAt', size: 150 }),
      hiCol.edNumberColumn({ header: '정렬', field: 'sortOrder', size: 70, align: 'center' }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 180 }),
    ],
    [openDeptLookup]
  );

  const orgColumns = useMemo(
    () => [
      // 겸임부서 — 팝업으로만 등록, 주관부서·기존 겸임부서는 팝업 목록에서 제외
      hiCol.edTextColumn({
        header: '겸임부서',
        field: 'deptName',
        size: 200,
        required: true,
        editable: false,
        showSearch: true,
        searchTitle: '부서 조회 (팝업)',
        title: '주관부서 외 겸직·중복 소속 부서',
        onSearch: ({ row }) => openDeptLookup('concurrent', row),
      }),
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 90 }),
      hiCol.edComboColumn({
        header: '소속상태',
        field: 'status',
        size: 100,
        options: MEMBERSHIP_STATUS_OPTIONS,
      }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 220 }),
    ],
    [openDeptLookup]
  );

  const userGrid = useHiGridHook({
    gridRef: userGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      accountUid: '',
      loginId: '',
      password: '',
      userNm: '',
      deptUid: '',
      deptCode: '',
      deptName: '',
      email: '',
      phone: '',
      accountType: 'USER',
      status: 'ACTIVE',
      failedLoginCnt: 0,
      lastLoginAt: '',
      sortOrder: 10,
      remark: '',
    },
  });

  const orgGrid = useHiGridHook({
    gridRef: orgGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      mapUid: '',
      accountUid: '',
      deptUid: '',
      deptCode: '',
      deptName: '',
      status: 'ACTIVE',
      remark: '',
    },
  });

  /** 구버전 저장분(부서코드·명 없음) 표시 보정 */
  const enrichOrgRow = useCallback(
    (row) => {
      const dept = deptRows.find((d) => d.deptUid === row.deptUid);
      return {
        ...row,
        deptCode: dept?.deptCode || row.deptCode || '',
        deptName: dept?.deptName || row.deptName || '',
      };
    },
    [deptRows]
  );

  /** 현재 선택 사용자의 편집 중 소속을 전체 매핑 캐시에 합친다. */
  const mergeCurrentOrgRows = useCallback(() => {
    const uid = selectedUidRef.current;
    if (!uid) return allOrgMapsRef.current;
    const current = orgGridRef.current?.getData?.() || [];
    const others = allOrgMapsRef.current.filter((r) => r.accountUid !== uid);
    allOrgMapsRef.current = [...others, ...current.map((r) => ({ ...r, accountUid: uid }))];
    return allOrgMapsRef.current;
  }, []);

  const selectUser = useCallback(
    (row) => {
      if (!row?.accountUid) return;
      if (selectedUidRef.current && selectedUidRef.current !== row.accountUid) {
        mergeCurrentOrgRows();
      }
      selectedUidRef.current = row.accountUid;
      setSelectedHint(
        `${row.loginId || '(ID 미입력)'} · ${row.userNm || ''}${row.deptName ? ` · 주관 ${row.deptName}` : ''}`
      );
      orgGridRef.current?.setData?.(
        allOrgMapsRef.current
          .filter((r) => r.accountUid === row.accountUid)
          .map((r) => enrichOrgRow(r))
      );
    },
    [mergeCurrentOrgRows, enrichOrgRow]
  );

  const handleSearch = useCallback(async () => {
    try {
      const users = await loadUsers(tenantCode, user);
      allOrgMapsRef.current = await loadUserOrgMaps(tenantCode);
      const filtered = filterUsers(users, getValues());
      userGridRef.current?.setData?.(filtered);

      const still = filtered.find((r) => r.accountUid === selectedUidRef.current);
      if (still) {
        selectUser(still);
        return;
      }
      selectedUidRef.current = '';
      setSelectedHint('사용자를 선택하면 겸임부서를 관리할 수 있습니다.');
      orgGridRef.current?.setData?.([]);
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [tenantCode, user, getValues, selectUser]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 최초 조회
  }, []);

  useEffect(() => {
    userGrid.onSelectRow = (row) => selectUser(row);
    userGrid.onRowClicked = ({ data }) => selectUser(data);
  }, [userGrid, selectUser]);

  const handleUserAdd = useCallback(async () => {
    const rows = userGridRef.current?.getData?.() || [];
    const maxSort = rows.reduce((max, r) => Math.max(max, Number(r.sortOrder) || 0), 0);
    const accountUid = createAccountUid();
    await userGrid.handleAdd({
      accountUid,
      id: accountUid,
      loginId: '',
      password: '',
      userNm: '',
      deptUid: '',
      deptCode: '',
      deptName: '',
      email: '',
      phone: '',
      accountType: 'USER',
      status: 'ACTIVE',
      failedLoginCnt: 0,
      lastLoginAt: '',
      sortOrder: Math.ceil((maxSort + 1) / 10) * 10,
      remark: '',
    });
    const added = (userGridRef.current?.getData?.() || []).find(
      (r) => r.accountUid === accountUid
    );
    if (added) selectUser(added);
  }, [userGrid, selectUser]);

  const handleUserDelete = useCallback(async () => {
    await userGrid.handleDelete();
  }, [userGrid]);

  const handleOrgAdd = useCallback(async () => {
    const accountUid = selectedUidRef.current;
    if (!accountUid) {
      showAlert('사용자를 먼저 선택하세요.', 'warning');
      return;
    }
    // 부서는 팝업(···)에서 선택 — 주관부서·기존 겸임부서는 팝업 목록에서 제외됨
    await orgGrid.handleAdd({
      mapUid: createAccountUid('UM'),
      accountUid,
      deptUid: '',
      deptCode: '',
      deptName: '',
      status: 'ACTIVE',
      remark: '',
    });
  }, [orgGrid]);

  const handleOrgDelete = useCallback(async () => {
    await orgGrid.handleDelete();
  }, [orgGrid]);

  const handleSave = useCallback(async () => {
    const gridUsers = userGridRef.current?.getData?.() || [];
    const activeUsers = gridUsers.filter((r) => r && r.mod !== 'D');
    const userValidation = validateUsers(tenantCode, activeUsers);
    if (!userValidation.ok) {
      showAlert(userValidation.message, 'warning');
      return;
    }

    mergeCurrentOrgRows();
    const deletedUserUids = new Set(
      gridUsers.filter((r) => r.mod === 'D').map((r) => r.accountUid)
    );

    const allUsers = await loadUsers(tenantCode, user);
    const filteredUids = new Set(filterUsers(allUsers, getValues()).map((r) => r.accountUid));
    const outside = allUsers.filter((r) => !filteredUids.has(r.accountUid));
    const nextUsers = [...outside, ...activeUsers];
    const nextUserUids = new Set(nextUsers.map((r) => r.accountUid));
    const nextOrgMaps = allOrgMapsRef.current.filter(
      (r) => !deletedUserUids.has(r.accountUid) && nextUserUids.has(r.accountUid)
    );

    const orgValidation = validateUserOrgMaps(
      nextOrgMaps.filter((r) => r.mod !== 'D'),
      nextUsers,
      deptRows
    );
    if (!orgValidation.ok) {
      showAlert(orgValidation.message, 'warning');
      return;
    }

    try {
      const { users: savedUsers, orgMaps } = await persistUsers(tenantCode, nextUsers, nextOrgMaps);
      allOrgMapsRef.current = orgMaps;
      userGridRef.current?.setData?.(filterUsers(savedUsers, getValues()));
      if (selectedUidRef.current && nextUserUids.has(selectedUidRef.current)) {
        orgGridRef.current?.setData?.(
          allOrgMapsRef.current.filter((r) => r.accountUid === selectedUidRef.current)
        );
      } else {
        selectedUidRef.current = '';
        orgGridRef.current?.setData?.([]);
      }
      showAlert('저장했습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
    }
  }, [tenantCode, user, getValues, mergeCurrentOrgRows, deptRows]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'loginId',
          label: '사용자ID',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'userNm',
          label: '사용자명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'status',
          label: '계정상태',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
          options: [{ value: '', label: '전체' }, ...ACCOUNT_STATUS_OPTIONS],
        },
      ],
    ],
    []
  );

  const getUserExportData = useCallback(() => {
    const rows = userGridRef.current?.getFilteredData?.() ?? userGridRef.current?.getData?.() ?? [];
    return rows.map(({ password, ...rest }) => rest);
  }, []);
  const getUserExportCols = useCallback(() => userGridRef.current?.getDataColumns?.() ?? [], []);
  const getOrgExportData = useCallback(
    () => orgGridRef.current?.getFilteredData?.() ?? orgGridRef.current?.getData?.() ?? [],
    []
  );
  const getOrgExportCols = useCallback(() => orgGridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: USER_GRID_ID,
    label: '사용자',
    getData: getUserExportData,
    getDataColumns: getUserExportCols,
    joinKeys: ['accountUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: ORG_GRID_ID,
    label: '사용자겸임부서',
    getData: getOrgExportData,
    getDataColumns: getOrgExportCols,
    joinKeys: ['mapUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '사용자관리',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'add',
        label: '추가',
        icon: '➕',
        onClick: handleActiveAdd,
        hotkey: 'F8',
        title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [
      handleSearch,
      handleActiveAdd,
      handleActiveDelete,
      handleSave,
      openExcelDialog,
      activeLabel,
    ]
  );

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>사용자관리</PageTitle>
        <Meta>기관 관리자 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <CollapsibleSearchPanel
          storageKey="master-user-manage"
          prefsUserId={prefsUserId}
          summary="사용자 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        활성 그리드: <strong style={{ color: theme.colors.primary }}>{activeLabel || '—'}</strong>
        {' · '}Alt+F2 순환{activeGridId ? ` · F8/F9 → ${activeLabel}` : ''}
        {' · '}기관 {tenantCode}
      </Meta>

      <Stack>
        <Panel $flex={1.35}>
          <ActiveGridHeader gridId={USER_GRID_ID} extra="기관 내 로그인 계정 · 사용자ID 저장 후 변경 불가">
            사용자
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={USER_GRID_ID}
              label="사용자"
              order={0}
              onAdd={handleUserAdd}
              onDelete={handleUserDelete}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={USER_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={userGridRef}
                  columns={userColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === USER_GRID_ID}
                  {...userGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="사용자"
                  gridId={USER_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={userGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={0.85}>
          <ActiveGridHeader
            gridId={ORG_GRID_ID}
            extra="주관부서 외 겸직·중복 소속 · 부서는 팝업으로 등록"
          >
            겸임부서
          </ActiveGridHeader>
          <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>
          <GridBody>
            <ActiveGridScope
              gridId={ORG_GRID_ID}
              label="겸임부서"
              order={1}
              onAdd={handleOrgAdd}
              onDelete={handleOrgDelete}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope gridId={ORG_GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                <HiGridComponent
                  ref={orgGridRef}
                  columns={orgColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === ORG_GRID_ID}
                  {...orgGrid.contextMenuProps}
                  enableHeaderMenu
                  enableColumnFilters
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="사용자겸임부서"
                  gridId={ORG_GRID_ID}
                  prefsUserId={prefsUserId}
                  colOptions={orgGrid.colOptions}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>
      </Stack>

      <CodeLookupDialog
        open={deptLookup.open}
        title={deptLookup.type === 'primary' ? '주관부서 조회' : '겸임부서 조회'}
        rows={deptLookup.rows || []}
        columns={deptLookupColumns}
        enableSearch
        searchConfig={{
          codeFields: ['deptCode'],
          nameFields: ['deptName'],
          codeLabel: '부서코드',
          nameLabel: '부서명',
        }}
        onSelect={applyDeptLookup}
        onClose={() => setDeptLookup({ open: false, type: null })}
      />

      {excelDialog}
    </PageContainer>
  );
};

const UserManage = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <UserManageInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default UserManage;

